import React, {useCallback, useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import maintenancesService from "../../../../services/web/maintenances-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import projectUtils from "../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {useQuery} from "@tanstack/react-query";

const initialFilters = {
    prefix: "",
    plate: "",
    name: "",
    interval_start_start: null,
    interval_start_end: null,
    interval_end_start: null,
    interval_end_end: null,
    mechanic: ""
};

const MaintenancesList = () => {
    const navigate = navigateHook();
    const gridRef = useRef(null);

    const [filter, setFilter] = useState([]);
    const [filterValues, setFilterValues] = useState(initialFilters);

    const {data, isLoading} = useQuery({
        queryFn: () => maintenancesService.list(filter),
        queryKey: ["list", {filter}]
    });

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const columnDefs = useMemo(() => [
        { headerName: "Prefixo", field: "prefix", width: 100 },
        { headerName: "Placa", field: "plate", width: 110, comparator: aggridUtils.nullLastComparator,
            cellRenderer: ({ value }) => <div className={"font-bold"}>{projectUtils.formatPlate(value)}</div>
        },
        { headerName: "Início", field: "date_start", width: 150, comparator: aggridUtils.agGridCompareDateTime, sort: "desc" },
        { headerName: "Término", field: "date_end", width: 150, comparator: aggridUtils.agGridCompareDateTime },
        { headerName: "Duração", field: "date_interval", width: 160,
            cellRenderer: ({ value }) => {
                if (!value) return "-";

                let m = Number(value);

                const y  = Math.floor(m / 525600); m %= 525600;
                const mo = Math.floor(m / 43200); m %= 43200;
                const d  = Math.floor(m / 1440); m %= 1440;
                const h  = Math.floor(m / 60); m %= 60;

                const parts = [
                    y  && `${y}a`,
                    mo && `${mo}m`,
                    d  && `${d}d`,
                    h  && `${h}h`,
                    m  && `${m}min`
                ].filter(Boolean);

                return parts.join(" ");
            }
        },
        { headerName: "Km início", field: "km_start", width: 120 },
        { headerName: "Km término", field: "km_end", width: 120, comparator: aggridUtils.nullLastComparator },
        { headerName: "Valor", field: "value_pay", width: 140,
            valueFormatter: ({ value }) => value ? Number(value).toLocaleString("pt-BR", {style: "currency", currency: "BRL"}) : "",
            comparator: (a, b, nodeA, nodeB, isInverted) => aggridUtils.nullLastComparator(a ? parseFloat(a) : a, b ? parseFloat(b) : b, nodeA, nodeB, isInverted) },
        { headerName: "Responsável", field: "name", width: 200 },
        { headerName: "Oficina", field: "mechanic", minWidth: 200, flex: 1 }
    ], []);

    const onChange = ({ target: { id, value } }) => {
        setFilterValues(prev => ({ ...prev, [id]: value }));
    };

    const apply = useCallback(() => {
        const intervals = [
            { label: "Início entre", from: filterValues.interval_start_start, to: filterValues.interval_start_end },
            { label: "Término entre", from: filterValues.interval_end_start, to: filterValues.interval_end_end }
        ];

        const invalid = intervals.find(({ from, to }) => !!from !== !!to);

        if (invalid) {
            projectUtils.swalErrorMessage('', `Os campos de "${invalid.label}" são obrigatórios`, 'Ok');
            return;
        }

        setFilter([
            {
                'field': 'prefix',
                'type': 'like',
                'value': filterValues.prefix
            },
            {
                'field': 'plate',
                'type': 'like',
                'value': filterValues.plate?.replace(/[.*+?^${}()|[\]\\-\s]/g, '')
            },
            {
                'field': 'date_start',
                'type': 'keywords',
                'value': {
                    dateStart: filterValues.interval_start_start?.toLocaleDateString(),
                    dateEnd: filterValues.interval_start_end?.toLocaleDateString()
                }
            },
            {
                'field': 'date_end',
                'type': 'keywords',
                'value': {
                    dateStart: filterValues.interval_end_start?.toLocaleDateString(),
                    dateEnd: filterValues.interval_end_end?.toLocaleDateString()
                }
            },
            {
                'field': 'name',
                'type': 'like',
                'value': filterValues.name
            },
            {
                'field': 'mechanic',
                'type': 'like',
                'value': filterValues.mechanic
            }
        ])
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, [])

    const newMaintenances = () => {
        const urlDev = "/maintenances/form";
        navigate(urlDev);
    };

    const onCellClicked = ({ data, event }) => {
        const urlDev = `/maintenances/form/${data.id}`;
        navigate(urlDev, event.ctrlKey);
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Manutenções</Template.Title>
                <Button onClick={newMaintenances}>
                    <FaPlus /> Adicionar manutenção
                </Button>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                            <InputForm
                                id="prefix"
                                label="Prefixo"
                                value={filterValues.prefix}
                                onChange={onChange}
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id="plate"
                                label="Placa"
                                value={filterValues.plate}
                                onChange={onChange}
                                mask={MaskDefault.plate}
                                classNameWrapper="col-span-2"
                                inputClass={"uppercase"}
                            />

                            <InputForm
                                id="name"
                                label="Responsável"
                                value={filterValues.name}
                                onChange={onChange}
                                classNameWrapper="col-span-8"
                            />

                            <div className={"col-span-4 flex items-stretch"}>
                                <InputDatetime
                                    id="date_interval_start"
                                    label="Início entre"
                                    selected={filterValues.interval_start_start}
                                    onChange={(date) => setFilterValues(prev => ({...prev, interval_start_start: date}))}
                                    dateFormat="L"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[47%]"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    id="dtEnd"
                                    selected={filterValues.interval_start_end}
                                    onChange={(date) => setFilterValues(prev => ({...prev, interval_start_end: date}))}
                                    dateFormat="L"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="w-[47%]"
                                />
                            </div>

                            <div className={"col-span-4 flex items-stretch"}>
                                <InputDatetime
                                    id="date_interval_end"
                                    label="Término entre"
                                    selected={filterValues.interval_end_start}
                                    onChange={(date) => setFilterValues(prev => ({...prev, interval_end_start: date}))}
                                    dateFormat="L"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[47%]"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    selected={filterValues.interval_end_end}
                                    onChange={(date) => setFilterValues(prev => ({...prev, interval_end_end: date}))}
                                    dateFormat="L"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="w-[47%]"
                                />
                            </div>

                            <div className="col-span-4 flex gap-4 items-end">
                                <InputForm
                                    id="mechanic"
                                    label="Oficina"
                                    value={filterValues.mechanic}
                                    onChange={onChange}
                                    classNameWrapper="flex-1"
                                />

                                <div className="flex gap-2">
                                    <Button target="primary-outline" onClick={clear} title={"Limpar"}>
                                        <FaEraser />
                                    </Button>

                                    <Button onClick={apply} title={"Buscar"}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className="h-[calc(100vh-197px)] aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                suppressCellFocus
                                animateRows={false}
                                onCellClicked={onCellClicked}
                                gridOptions={{ localeText: aggridUtils.localization() }}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default MaintenancesList;